import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './SchoolDiscovery.css';

interface SchoolItem {
  id: string;
  teacher_id: string;
  name: string;
  name_fa?: string;
  description?: string;
  description_fa?: string;
  category?: string;
  status: string;
  created_at: string;
  subscriber_count: number;
  content_count: number;
  is_subscribed: boolean;
}

export function SchoolDiscovery() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, isLoading: authLoading, accessToken } = useAuth();
  
  const [schools, setSchools] = useState<SchoolItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [subscribingId, setSubscribingId] = useState<string | null>(null);

  const isRTL = i18n.language === 'fa';

  const categories = [
    { value: '', label: i18n.language === 'fa' ? 'همه دسته‌ها' : 'All Categories' },
    { value: 'math', label: t('schools.categories.math') },
    { value: 'science', label: t('schools.categories.science') },
    { value: 'language', label: t('schools.categories.language') },
    { value: 'history', label: t('schools.categories.history') },
    { value: 'arts', label: t('schools.categories.arts') },
    { value: 'technology', label: t('schools.categories.technology') },
    { value: 'other', label: t('schools.categories.other') }
  ];

  const fetchSchools = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const url = new URL('/api/discover/schools', window.location.origin);
      if (searchQuery) url.searchParams.set('search', searchQuery);
      if (selectedCategory) url.searchParams.set('category', selectedCategory);

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(url.toString(), { headers });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch schools');
      }

      setSchools(data.data?.schools || []);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, searchQuery, selectedCategory]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSchools();
      
      // Update URL params
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (selectedCategory) params.set('category', selectedCategory);
      setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchSchools, searchQuery, selectedCategory, setSearchParams]);

  const handleSubscribe = async (schoolId: string) => {
    if (!accessToken) {
      navigate('/login?redirect=/schools');
      return;
    }

    setSubscribingId(schoolId);

    try {
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ school_id: schoolId })
      });

      const data = await response.json();

      if (response.ok) {
        setSchools(prev => prev.map(s => 
          s.id === schoolId 
            ? { ...s, is_subscribed: true, subscriber_count: s.subscriber_count + 1 } 
            : s
        ));
      } else {
        throw new Error(data.error?.message || 'Failed to subscribe');
      }
    } catch (err) {
      console.error('Error subscribing:', err);
      alert(i18n.language === 'fa' ? 'خطا در عضویت' : 'Failed to subscribe');
    } finally {
      setSubscribingId(null);
    }
  };

  const handleUnsubscribe = async (schoolId: string) => {
    if (!accessToken) return;

    setSubscribingId(schoolId);

    try {
      // First get the subscription ID
      const subsResponse = await fetch('/api/subscriptions', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const subsData = await subsResponse.json();
      const subscription = subsData.data?.subscriptions?.find(
        (s: { school_id: string }) => s.school_id === schoolId
      );

      if (!subscription) {
        throw new Error('Subscription not found');
      }

      const response = await fetch(`/api/subscriptions/${subscription.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSchools(prev => prev.map(s => 
          s.id === schoolId 
            ? { ...s, is_subscribed: false, subscriber_count: Math.max(0, s.subscriber_count - 1) } 
            : s
        ));
      } else {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to unsubscribe');
      }
    } catch (err) {
      console.error('Error unsubscribing:', err);
      alert(i18n.language === 'fa' ? 'خطا در لغو عضویت' : 'Failed to unsubscribe');
    } finally {
      setSubscribingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className={`school-discovery ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="sd-page-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className={`school-discovery ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="sd-page-header">
        <div className="sd-page-header-content">
          <a href="/" className="sd-page-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </a>
          <div className="sd-page-header-actions">
            <LanguageSwitcher />
            {user ? (
              <>
                <button 
                  onClick={() => navigate(user.role === 'teacher' ? '/teacher' : '/student')} 
                  className="sd-page-dashboard-btn"
                >
                  {i18n.language === 'fa' ? 'داشبورد' : 'Dashboard'}
                </button>
                <button onClick={handleLogout} className="sd-page-logout-btn">
                  {t('common.logout')}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="sd-page-login-btn">
                  {t('common.login')}
                </button>
                <button onClick={() => navigate('/register')} className="sd-page-register-btn">
                  {t('common.register')}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="sd-page-main">
        <div className="sd-page-container">
          {/* Page Title */}
          <div className="sd-page-title-section">
            <h1>{i18n.language === 'fa' ? 'کشف مدارس مجازی' : 'Discover Virtual Schools'}</h1>
            <p>
              {i18n.language === 'fa' 
                ? 'مدارس مجازی را کشف کنید و عضو شوید تا به محتوای آموزشی دسترسی داشته باشید.'
                : 'Discover virtual schools and subscribe to access educational content.'}
            </p>
          </div>

          {/* Filters */}
          <div className="sd-page-filters">
            <div className="sd-page-search-box">
              <input
                type="text"
                placeholder={i18n.language === 'fa' ? 'جستجوی مدرسه...' : 'Search schools...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="sd-page-search-input"
              />
              <span className="sd-page-search-icon">🔍</span>
            </div>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="sd-page-category-select"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="sd-page-error">{error}</div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="sd-page-loading-content">
              {i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}
            </div>
          ) : schools.length === 0 ? (
            <div className="sd-page-empty">
              <div className="sd-page-empty-icon">🔍</div>
              <h3>{i18n.language === 'fa' ? 'مدرسه‌ای یافت نشد' : 'No schools found'}</h3>
              <p>
                {i18n.language === 'fa'
                  ? 'جستجوی خود را تغییر دهید یا فیلترها را پاک کنید.'
                  : 'Try a different search or clear filters.'}
              </p>
              {(searchQuery || selectedCategory) && (
                <button 
                  className="sd-page-clear-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('');
                  }}
                >
                  {i18n.language === 'fa' ? 'پاک کردن فیلترها' : 'Clear Filters'}
                </button>
              )}
            </div>
          ) : (
            <div className="sd-page-schools-grid">
              {schools.map(school => (
                <SchoolCard 
                  key={school.id} 
                  school={school}
                  isSubscribing={subscribingId === school.id}
                  onSubscribe={() => handleSubscribe(school.id)}
                  onUnsubscribe={() => handleUnsubscribe(school.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="sd-page-footer">
        <div className="sd-page-footer-content">
          <p>
            {i18n.language === 'fa' 
              ? '© ۲۰۲۵ سولرمکتب. تمامی حقوق محفوظ است.'
              : '© 2025 SolarMaktab. All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
}

// School Card Component
function SchoolCard({ 
  school,
  isSubscribing,
  onSubscribe,
  onUnsubscribe
}: { 
  school: SchoolItem;
  isSubscribing: boolean;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
}) {
  const { t, i18n } = useTranslation();
  
  const displayName = i18n.language === 'fa' && school.name_fa ? school.name_fa : school.name;
  const displayDescription = i18n.language === 'fa' && school.description_fa 
    ? school.description_fa 
    : school.description;
  const categoryLabel = school.category ? t(`schools.categories.${school.category}`) : null;

  return (
    <div className={`sd-page-school-card ${school.is_subscribed ? 'subscribed' : ''}`}>
      <div className="sd-page-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="sd-page-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="sd-page-school-description">{displayDescription}</p>
      )}

      <div className="sd-page-school-stats">
        <div className="sd-page-stat">
          <span className="sd-page-stat-value">{school.subscriber_count}</span>
          <span className="sd-page-stat-label">{t('schools.subscribers')}</span>
        </div>
        <div className="sd-page-stat">
          <span className="sd-page-stat-value">{school.content_count}</span>
          <span className="sd-page-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="sd-page-school-actions">
        {school.is_subscribed ? (
          <button 
            className="sd-page-action-btn danger"
            onClick={onUnsubscribe}
            disabled={isSubscribing}
          >
            {isSubscribing 
              ? (i18n.language === 'fa' ? '...' : '...') 
              : t('schools.unsubscribe')}
          </button>
        ) : (
          <button 
            className="sd-page-action-btn primary"
            onClick={onSubscribe}
            disabled={isSubscribing}
          >
            {isSubscribing 
              ? (i18n.language === 'fa' ? '...' : '...') 
              : t('schools.subscribe')}
          </button>
        )}
      </div>
    </div>
  );
}
